// Runtime for working homes, pets, railways, machines, farming and the sky quest.
import * as THREE from "three";
import { B, BLOCKS } from "./blocks.js";
import {
  ITEMS,
  amount,
  addItem,
  selectedItem,
  assignSlot,
} from "./crafting.js";
import {
  DEVICE_IDS,
  LANDMARKS,
  PUZZLES,
  cellKey,
  deviceState,
  canRemoveDevice,
  transact,
  transferChest,
  solvePuzzle,
  circuitPower,
  railNeighbours,
  nextQuest,
} from "./expansion-rules.js";
import { nameTag } from "./models.js";
import { animalModel, arrowModel } from "./craft-models.js";
import { disposeObj } from "./entities.js";
import { boxFree, moveBody } from "./physics.js";
import { audio } from "./audio.js";
import { escapeHtml } from "./util.js";
import { FirstPersonHand } from "./first-person.js";
const v3 = (x = 0, y = 0, z = 0) => new THREE.Vector3(x, y, z);
const btn = (label, op, extra = "", cls = "btn-blue") =>
  `<button class="btn ${cls}" data-act="expansion" data-op="${op}" ${extra}>${label}</button>`;
function box(parent, size, pos, colour) {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(...size),
    new THREE.MeshLambertMaterial({ color: colour }),
  );
  m.position.set(...pos);
  m.castShadow = true;
  parent.add(m);
  return m;
}
export function cartModel() {
  const g = new THREE.Group();
  box(g, [1.2, 0.65, 1.4], [0, 0.45, 0], "#8696a8");
  box(g, [1, 0.2, 1.2], [0, 0.85, 0], "#513c32");
  for (const x of [-0.65, 0.65])
    for (const z of [-0.45, 0.45])
      box(g, [0.15, 0.35, 0.35], [x, 0.18, z], "#2b3548");
  return g;
}
// Adult Ember has a saddle, horns, a stepped tail and articulated wings.
// A separate model keeps the small following pet light and readable.
export function dragonMountModel() {
  const g = new THREE.Group(),
    red = "#c94335",
    dark = "#902b35",
    gold = "#f8bd67";
  box(g, [1.05, 0.8, 1.75], [0, 0.68, 0], red);
  box(g, [0.75, 0.45, 1.6], [0, 0.36, 0.08], gold);
  box(g, [0.58, 0.8, 0.65], [0, 1.08, 0.92], red);
  box(g, [0.88, 0.65, 0.92], [0, 1.5, 1.3], red);
  box(g, [0.75, 0.32, 0.55], [0, 1.33, 1.96], gold);
  box(g, [0.77, 0.07, 0.45], [0, 1.2, 1.97], dark);
  for (const side of [-1, 1]) {
    box(g, [0.18, 0.48, 0.2], [side * 0.3, 1.99, 1.12], "#ffedc4");
    box(g, [0.08, 0.18, 0.24], [side * 0.46, 1.62, 1.53], "#fff5cf");
    box(g, [0.09, 0.12, 0.1], [side * 0.49, 1.62, 1.59], "#243842");
    for (const z of [-0.52, 0.55]) {
      box(g, [0.24, 0.52, 0.27], [side * 0.48, 0.18, z], dark);
      box(g, [0.36, 0.15, 0.46], [side * 0.48, -0.08, z + 0.12], red);
      box(g, [0.28, 0.12, 0.14], [side * 0.48, -0.08, z + 0.34], "#ffe8bc");
    }
  }
  for (let i = 0; i < 4; i++) {
    box(
      g,
      [0.55 - i * 0.1, 0.34 - i * 0.05, 0.6],
      [0, 0.58 - i * 0.06, -1.05 - i * 0.47],
      i % 2 ? dark : red,
    );
    box(g, [0.12, 0.21, 0.17], [0, 1.1, -0.55 + i * 0.35], gold);
  }
  box(g, [0.9, 0.12, 0.7], [0, 1.14, -0.12], "#80513f");
  box(g, [0.92, 0.22, 0.14], [0, 1.28, -0.52], "#503e3d");
  box(g, [0.18, 0.18, 0.18], [0, 1.26, 0.28], "#e9c26c");
  const wings = [];
  for (const side of [-1, 1]) {
    const w = new THREE.Group();
    w.position.set(side * 0.45, 0.94, -0.15);
    for (let i = 0; i < 4; i++) {
      box(
        w,
        [0.48, 0.065, 1.65 - i * 0.28],
        [side * (0.23 + i * 0.44), 0, -i * 0.12],
        i % 2 ? "#df8147" : "#f5ad5d",
      );
      box(
        w,
        [0.48, 0.1, 0.09],
        [side * (0.23 + i * 0.44), 0.025, 0.81 - i * 0.26],
        dark,
      );
    }
    box(w, [1.6, 0.1, 0.09], [side * 0.8, 0.02, -0.6], red);
    g.add(w);
    wings.push(w);
  }
  g.userData.wings = wings;
  return g;
}
const SHARED_DEVICES = new Set([B.DOOR, B.SWITCH, B.DRAWBRIDGE]);
// Bow tuning. Hits use each monster's own hitbox, so any size of monster works.
export const BOW = {
  speed: 24, // blocks per second
  life: 2, // seconds of flight before an arrow drops
  damage: 3,
  cooldown: 0.6,
  range: 14, // aim help reach in blocks
  cone: Math.cos((35 * Math.PI) / 180), // aim help: ±35° of the camera
  pickup: 1.3, // blocks from the player's body to collect a dropped arrow
  keep: 12, // dropped arrows kept at once (oldest go first)
  lasts: 60, // seconds of play a dropped arrow stays
  ready: 0.4, // seconds before a fresh drop can be collected
};
// Where aim help points: the middle of the monster's body.
export const aimPoint = (m) => v3(m.body.pos.x, m.body.pos.y + m.body.h * 0.5, m.body.pos.z);
// Where along the straight path a→b (0…1) the arrow enters monster m's box, or -1 if it misses. The box is
// padded (0.35 each side, 0.2 below, 0.3 above) and the whole path is tested, so a fast arrow can never skip
// through a thin zombie between frames.
export function arrowEntry(a, b, m) {
  const p = m.body.pos,
    w = m.body.hw + 0.35,
    lo = [p.x - w, p.y - 0.2, p.z - w],
    hi = [p.x + w, p.y + m.body.h + 0.3, p.z + w],
    s = [a.x, a.y, a.z],
    e = [b.x, b.y, b.z];
  let t0 = 0,
    t1 = 1;
  for (let i = 0; i < 3; i++) {
    const d = e[i] - s[i];
    if (Math.abs(d) < 1e-9) {
      if (s[i] <= lo[i] || s[i] >= hi[i]) return -1;
      continue;
    }
    const u = (lo[i] - s[i]) / d,
      v = (hi[i] - s[i]) / d;
    t0 = Math.max(t0, Math.min(u, v));
    t1 = Math.min(t1, Math.max(u, v));
    if (t0 >= t1) return -1;
  }
  return t0;
}
export const arrowHits = (a, b, m) => arrowEntry(a, b, m) >= 0;
export class Expansion {
  constructor(E) {
    this.E = E;
    this.game = E.game;
    this.d = E.data;
    this.a = this.d.expansion;
    this.group = new THREE.Group();
    E.group.add(this.group);
    this.visuals = new Map();
    this.markers = [];
    this.farmModels = new Map();
    this.arrowShots = [];
    this.arrowDrops = [];
    this.aim = null;
    this.time = 0;
    this.mount = null;
    this.rail = null;
    this.panel = null;
    this.tick = -1;
    this.gearKey = "";
  }
  build() {
    for (const [id, def] of Object.entries(LANDMARKS)) {
      if (id === "vault") continue;
      const g = new THREE.Group();
      g.position.set(def.x + 0.5, def.y, def.z + 0.5);
      const base = box(
        g,
        [1.2, 0.25, 1.2],
        [0, 0.12, 0],
        id === "sky" ? "#b981fa" : "#dbb46c",
      );
      const tag = nameTag(def.name.toUpperCase(), {
        color: "#ffe6a5",
        crown: false,
        scale: 2.6,
      });
      tag.position.y = 2.7;
      g.add(tag);
      const jewel = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.35),
        new THREE.MeshLambertMaterial({
          color: id === "sky" ? "#aaddff" : "#ffc55d",
          emissive: "#4d2f17",
        }),
      );
      jewel.position.y = 1.5;
      g.add(jewel);
      this.group.add(g);
      this.markers.push({ id, def, g, tag, jewel });
    }
    const m = LANDMARKS.vault,
      g = new THREE.Group();
    g.position.set(m.x + 0.5, m.y, m.z + 0.5);
    box(g, [1.4, 2.7, 0.3], [0, 1.3, 0], "#aa7ee2");
    const tag = nameTag("CRYSTAL CLUE", {
      color: "#e5c5ff",
      crown: false,
      scale: 2,
    });
    tag.position.y = 3;
    g.add(tag);
    this.group.add(g);
    this.markers.push({ id: "vault", def: m, g, tag });
    this.cart = cartModel();
    this.cart.visible = false;
    this.group.add(this.cart);
    this.dragon = dragonMountModel();
    this.dragon.visible = false;
    this.group.add(this.dragon);
    this.light = new THREE.PointLight("#ffce82", 0, 15, 1.6);
    this.group.add(this.light);
    const segments = [];
    for (let face = 0; face < 6; face++)
      for (let i = 0; i < 10; i++) {
        const a = [
            -0.4 + (i % 4) * 0.23,
            -0.4 + Math.floor(i / 4) * 0.32,
            0.506,
          ],
          b = [a[0] + 0.16, a[1] + (i % 2 ? 0.18 : -0.14), 0.506];
        for (const pt of [a, b]) {
          const q =
            face % 3 === 0
              ? [pt[2], pt[0], pt[1]]
              : face % 3 === 1
                ? [pt[0], pt[2], pt[1]]
                : pt;
          if (face > 2) q[face % 3] *= -1;
          segments.push(...q);
        }
      }
    const crackGeo = new THREE.BufferGeometry();
    crackGeo.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(segments, 3),
    );
    this.cracks = new THREE.LineSegments(
      crackGeo,
      new THREE.LineBasicMaterial({
        color: "#302739",
        transparent: true,
        opacity: 0.8,
      }),
    );
    this.cracks.visible = false;
    this.group.add(this.cracks);
    this.cross = document.createElement("div");
    this.cross.className = "aim-cross";
    this.cross.textContent = "＋";
    this.game.ui.root.append(this.cross);
    // Third-person aim help: one pulsing gold ring, moved under the bow's target.
    this.aimRing = new THREE.Mesh(
      new THREE.RingGeometry(0.68, 1, 40).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color("#ffd24a").multiplyScalar(1.4),
        transparent: true,
        opacity: 0.8,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    this.aimRing.visible = false;
    this.group.add(this.aimRing);
    this.syncDevices();
    this.applyGear();
  }
  persist() {
    this.game.save.persist();
    this.E.syncHeldItem();
    this.E.refreshHUD();
    this.applyGear();
  }
  toast(text) {
    this.game.ui.toast(text, "blue", 4000);
  }
  // A sign nobody has written on says whose home it is.
  homeSign() {
    return `${this.game.save.data.name}’s home`;
  }
  panelHTML(title, body) {
    return `<div class="panel expansion-panel"><div class="inventory-head"><h2>${title}</h2>${btn("Back to playing →", "close", "", "btn-green")}</div>${body}</div>`;
  }
  open(title, body) {
    if (this.E.state !== "play") return;
    this.E.cancelGather();
    this.game.paused = true;
    this.game.input.reset();
    audio.setDuck(true);
    this.game.ui.modal(this.panelHTML(title, body), "modal-expansion");
  }
  close() {
    this.panel = null;
    this.game.resume();
    this.E.player.invuln = Math.max(2, this.E.player.invuln);
  }
  questPanel() {
    this.panel = { type: "quest" };
    const q = nextQuest(this.d, this.game.save.data.name),
      f = this.a.flags;
    this.open(
      "🐉 Ember’s First Flight",
      `<div class="quest-hero"><span>${q.target?.icon || "🐉"}</span><h3>${escapeHtml(q.title)}</h3><p>${escapeHtml(q.text)}</p>${btn("🔊 Read my quest", "readQuest")}${q.target ? btn("Show me the way →", "questTrail", "", "btn-green") : btn("Open my learning map", "map")}</div><p>Build, discover and learn at your own pace. Abilities stay yours once earned.</p><div class="expansion-grid">${[
        ["cave", "⛏️ Crystal Mine"],
        ["farm", "🌱 Garden"],
        ["machines", "💡 Machines"],
        ["railway", "🛤️ Railway"],
        ["perch", "🐉 Ember"],
        ["sky", "☁️ Sky Garden"],
      ]
        .map(([id, name]) => btn(name, "trail", `data-id="${id}"`))
        .join(
          "",
        )}</div><p>${f.flew ? "✓ First flight earned!" : this.d.dragonHatched ? "✓ Ember hatched" : "🥚 Collect 6 different learning stamps to hatch Ember"}</p>${btn("🐾 Scout: find treasure", "scout")}${btn("Gear & enchantments", "gear")}${btn(this.a.firstPerson ? "👀 Switch to third person" : "👀 Try first person", "camera")}`,
    );
  }
  puzzle(id) {
    const p = PUZZLES[id];
    if (!p) return;
    if (this.a.flags[p.flag]) {
      this.toast("You already learned this magic. It is yours to keep!");
      return;
    }
    this.panel = { type: "puzzle", id, attempts: 0 };
    this.renderPuzzle();
  }
  renderPuzzle(message = "") {
    const p = PUZZLES[this.panel.id];
    this.open(
      `${p.icon} ${p.name}`,
      `<p class="puzzle-story">${p.text}</p><div class="puzzle-choices">${p.choices.map((c) => btn(escapeHtml(String(c)), "answer", `data-answer="${escapeHtml(String(c))}"`)).join("")}</div><p class="puzzle-feedback" role="status">${escapeHtml(message)}</p>${btn("💡 Hint", "hint")}${btn("🔊 Read it to me", "readPuzzle")}<p>No timer. Try an idea, and use a hint whenever you like.</p>`,
    );
  }
  answer(value) {
    if (this.panel?.type !== "puzzle") return false;
    const p = PUZZLES[this.panel.id];
    if (value !== String(p.answer)) {
      this.panel.attempts++;
      this.renderPuzzle(`Let’s try again. ${p.hint}`);
      return false;
    }
    if (!solvePuzzle(this.game.save.data, this.panel.id, value)) {
      this.renderPuzzle(
        "Make a little space in your backpack, then try again.",
      );
      return false;
    }
    audio.play("unlock");
    this.open(
      "✨ You worked it out!",
      `<p class="puzzle-story">${p.explain}</p><p>+20 coins · +1 gem${Object.entries(
        p.reward,
      )
        .map(([id, n]) => ` · ${n} ${ITEMS[id].name}`)
        .join("")}</p>${btn("Keep exploring →", "close", "", "btn-green")}`,
    );
    this.panel = { type: "reward" };
    this.persist();
    return true;
  }
  gearPanel() {
    this.panel = { type: "gear" };
    const a = this.a;
    this.open(
      "✨ My adventure gear",
      `<p>Craft equipment at a table, then choose it here. Armour and cloud boots work in Adventure too.</p><div class="expansion-grid">${["armour_iron", "armour_diamond", "jump_boots", "shield"].map((id) => (amount(this.d, id) ? btn(`${ITEMS[id].icon} ${ITEMS[id].name}${a.gear.armour === id || (id === "jump_boots" && a.gear.boots) || (id === "shield" && a.gear.shield) ? " ✓" : ""}`, "equip", `data-id="${id}"`) : `<div class="locked-gear">${ITEMS[id].icon} Craft ${ITEMS[id].name.toLowerCase()} to equip it</div>`)).join("")}</div><p>Iron armour blocks every third hit. Diamond armour blocks every second. Your shield blocks hits while you hold Use; cloud boots add a double jump.</p><h3>Enchant at an enchanting table</h3><p>Frost slows monsters. Glow pickaxes gather one extra resource and light caves.</p>${[
        ["frost", "❄️ Frost sword"],
        ["fortune", "✨ Glow pickaxe"],
      ]
        .map(
          ([id, name]) =>
            `<article><strong>${name}${a.enchant[id] ? " ✓" : ""}</strong><p>${a.flags[id] ? "Magic learned · 100 coins + 3 gems to enchant" : "First learn this enchantment’s puzzle."}</p>${btn(a.enchant[id] ? "Already enchanted" : a.flags[id] ? "Apply magic" : "Learn magic", "enchant", `data-id="${id}" ${a.enchant[id] ? "disabled" : ""}`)}</article>`,
        )
        .join("")}`,
    );
  }
  nearestEnchant() {
    return this.d.blocks.some(
      (b) =>
        b[3] === B.ENCHANT_TABLE &&
        this.E.player.pos.distanceTo(v3(b[0] + 0.5, b[1], b[2] + 0.5)) < 4.5,
    );
  }
  applyGear() {
    const key = JSON.stringify(this.a.gear);
    if (key === this.gearKey) return;
    this.gearKey = key;
    const c = this.E.character;
    if (this.armour) {
      this.armour.removeFromParent();
      disposeObj(this.armour);
    }
    this.armour = new THREE.Group();
    c.model.add(this.armour);
    const col = this.a.gear.armour === "armour_diamond" ? "#71e8e8" : "#c6d4df";
    if (this.a.gear.armour) {
      box(this.armour, [0.55, 0.55, 0.32], [0, 1, 0], col);
      box(this.armour, [0.51, 0.12, 0.51], [0, 1.82, 0], col);
    }
    if (this.a.gear.boots)
      for (const x of [-0.12, 0.12])
        box(this.armour, [0.24, 0.16, 0.35], [x, 0.1, 0.03], "#bbdafa");
  }
  context(cell) {
    if (this.mount) return { label: "🐉 Land Ember", op: "land" };
    if (this.rail) return { label: "🛤️ Leave minecart", op: "exitCart" };
    const b = cell && this.d.blocks.find((b) => cellKey(b) === cellKey(cell));
    if (
      b &&
      DEVICE_IDS.has(b[3]) &&
      (!this.E.session || SHARED_DEVICES.has(b[3])) &&
      this.E.player.pos.distanceTo(v3(b[0] + 0.5, b[1], b[2] + 0.5)) < 4.5
    )
      return {
        label: `Use ${ITEMS[Object.keys(ITEMS).find((id) => ITEMS[id].block === b[3])]?.name || "block"}`,
        op: "device",
        key: cellKey(b),
      };
    const friend = this.a.animals.find(
      (a) => v3(...a.pos).distanceTo(this.E.player.pos) < 3,
    );
    if (friend)
      return { label: "Care for your " + friend.kind, op: "farmAnimal" };
    if (
      this.scoutTreasure &&
      this.scoutTreasure.distanceTo(this.E.player.pos) < 3
    )
      return { label: "🐾 Dig up Scout’s treasure", op: "scoutLoot" };
    let marker = this.markers.find(
      (m) => m.g.visible && this.E.player.pos.distanceTo(m.g.position) < 3.5,
    );
    if (marker)
      return {
        label: marker.def.icon + " " + marker.def.name,
        op: "marker",
        id: marker.id,
      };
    return null;
  }
  interact(ctx) {
    if (!ctx || this.game.paused || this.E.state !== "play") return false;
    if (ctx.op === "land") {
      this.land();
      return true;
    }
    if (ctx.op === "exitCart") {
      this.exitCart();
      return true;
    }
    if (ctx.op === "device") {
      this.device(ctx.key);
      return true;
    }
    if (ctx.op === "scoutLoot") {
      this.useHeld();
      return true;
    }
    if (ctx.op === "farmAnimal") {
      this.farmAction();
      return true;
    }
    if (ctx.op === "marker") {
      this.marker(ctx.id);
      return true;
    }
    return false;
  }
  marker(id) {
    if (id === "vault") return this.puzzle("cave");
    if (id === "cave") {
      this.E.waypoint = LANDMARKS.vault;
      this.toast(
        "Go down the torch-lit stairs. The sparkling door has a clue.",
      );
      return;
    }
    if (id === "farm")
      return this.a.flags.farmLesson ? this.farmHelp() : this.puzzle("farm");
    if (id === "machines")
      return this.a.flags.circuit ? this.machineHelp() : this.puzzle("circuit");
    if (id === "railway")
      return this.a.flags.railLesson ? this.railHelp() : this.puzzle("rail");
    if (id === "perch") return this.dragonPanel();
    if (id === "sky" || id === "moon") {
      if (id === "sky" && !this.a.flags.sky) {
        if (!transact(this.game.save.data, {}, { diamond: 3, crystal: 4 }))
          return this.toast(
            "Use some supplies to make space for your sky treasure.",
          );
        this.a.flags.sky = true;
        this.persist();
        audio.play("unlock");
        this.E.fx.particles.confetti(this.E.player.pos, 65, 1.4);
      }
      this.panel = { type: "sky" };
      return this.open(
        id === "sky"
          ? `☁️ ${escapeHtml(this.E.session?.ownerName || this.game.save.data.name)}’s Sky Garden`
          : "🌙 Moonflower Island",
        `<p>You flew above the clouds! Build a sky home here. The portal links your two sky gardens. Step through to visit the other island.</p>${btn("Step through the portal", "portal", "", "btn-green")}${btn("Ride Ember again", "mount")}`,
      );
    }
  }
  dragonPanel() {
    this.panel = { type: "dragon" };
    const ready =
      this.d.dragonHatched &&
      this.a.flags.cave &&
      amount(this.d, "saddle") &&
      this.d.blocks.some((b) => b[3] === B.PERCH);
    this.open(
      "🐉 Ember’s flight school",
      `<p>${!this.d.dragonHatched ? "First hatch Ember with all 6 learning stamps." : !this.a.flags.cave ? "Ember wants to find the treasure in Crystal Mine." : !this.d.blocks.some((b) => b[3] === B.PERCH) ? "Build a dragon perch beside your home." : !amount(this.d, "saddle") ? "Craft a saddle with 4 hide, 4 wool and 4 crystals." : "Ember is ready to carry you!"}</p>${ready ? btn(this.a.flags.flightLesson ? "Ride Ember!" : "Learn the sky route", this.a.flags.flightLesson ? "mount" : "flightLesson", "", "btn-green") : btn("Show my next quest", "quests")}<p>While flying: steer with the movement controls. Hold Jump to rise. Use / Land descends safely. Nothing uses up your saddle.</p>${this.d.dragonHatched ? btn("Ask Ember for a trick ✨", "trick") : ""}`,
    );
  }
  farmHelp() {
    this.open(
      "🌱 Your garden",
      `<p>Craft garden beds and place them outdoors. Use a bed to plant a seed, then use it again to water. A watered crop grows in 45 seconds of playing.</p><p>Craft a watering can from 2 wood. Wheat feeds farm animals and makes bread. Use a lead on a sheep or cow to adopt it; two fed adults of the same kind have a baby. Shears collect wool without hurting sheep.</p>${btn("Back to my garden", "close", "", "btn-green")}`,
    );
    this.panel = { type: "help" };
  }
  machineHelp() {
    this.open(
      "💡 Make your own machine",
      `<p>Place a switch next to glow wire. Connect the wire to a magic lamp, door or drawbridge. Use the switch to turn the whole connected circuit on or off.</p><p>Build a secret entrance, a bridge over water, or a row of lamps. Wires connect face to face, including up and down.</p>${btn("Try building it", "close", "", "btn-green")}`,
    );
    this.panel = { type: "help" };
  }
  railHelp() {
    this.open(
      "🛤️ Make a railway",
      `<p>Craft rails and a minecart. Place rails side by side on the ground or on blocks. Tracks can turn and climb one block at a time. Use a rail to ride.</p><p>Powered rails speed you up. At a fork, steer left or right. Use / Leave gets out safely. Put rails on a bridge to make a roller coaster!</p>${btn("Build my railway", "close", "", "btn-green")}`,
    );
    this.panel = { type: "help" };
  }
  device(key) {
    const b = this.d.blocks.find((b) => cellKey(b) === key);
    if (
      !b ||
      this.E.player.pos.distanceTo(v3(b[0] + 0.5, b[1], b[2] + 0.5)) > 4.5
    )
      return;
    const s = deviceState(this.d, b),
      id = b[3];
    // Visitors can open doors and flip switches, but chests, beds and machines stay the owner's.
    if (this.E.session && !SHARED_DEVICES.has(id))
      return this.toast(`That belongs to ${escapeHtml(this.E.session.ownerName)}. Doors and switches work for everyone.`);
    this.panel = { type: "device", key };
    if (id === B.DOOR || id === B.SWITCH || id === B.DRAWBRIDGE) {
      s.on = !s.on;
      const flip = (c) => this.E.recordOp({ k: "device", x: c[0], y: c[1], z: c[2], on: s.on ? 1 : 0, prevOn: s.on ? 0 : 1 });
      flip(b);
      if (id === B.DOOR)
        for (const q of this.d.blocks)
          if (
            q[3] === B.DOOR &&
            q[0] === b[0] &&
            q[2] === b[2] &&
            Math.abs(q[1] - b[1]) === 1
          ) {
            deviceState(this.d, q).on = s.on;
            flip(q);
          }
      this.syncDevices();
      audio.play("pop");
      this.persist();
      return;
    }
    if (id === B.BED) {
      const spots = [
        [b[0] + 1.5, b[1] + 1, b[2] + 0.5],
        [b[0] - 0.5, b[1] + 1, b[2] + 0.5],
        [b[0] + 0.5, b[1] + 1, b[2] + 0.5],
      ];
      const pos = spots.find((p) =>
        boxFree(
          this.E.world,
          p[0] - 0.31,
          p[1],
          p[2] - 0.31,
          p[0] + 0.31,
          p[1] + 1.8,
          p[2] + 0.31,
        ),
      );
      if (!pos) return this.toast("Leave some space beside your bed.");
      this.a.home = pos;
      this.d.worldTime = Math.ceil(this.d.worldTime / 480) * 480 + 20;
      this.E.player.heal(5);
      this.d.hearts = 5;
      this.persist();
      this.toast(
        "Morning! This bed is now your home. Town still takes you to the village.",
      );
      return;
    }
    if (id === B.PERCH) return this.dragonPanel();
    if (id === B.ENCHANT_TABLE) return this.gearPanel();
    if (id === B.RAIL || id === B.POWERED_RAIL) {
      if (!amount(this.d, "minecart"))
        return this.toast("Craft a minecart at a table first.");
      this.startCart(b);
      return;
    }
    if (id === B.FARM_PLOT) {
      if (s.planted === null) {
        if (!transact(this.game.save.data, { seeds: 1 }))
          return this.toast("Craft seeds from leaves first.");
        s.planted = this.d.worldTime;
        s.water = false;
        this.toast(
          "Seed planted! Use this bed again with a watering can in your backpack.",
        );
      } else if (!s.water) {
        if (!amount(this.d, "watering_can"))
          return this.toast("Craft a watering can from 2 wood.");
        s.water = true;
        s.planted = this.d.worldTime;
        this.toast("Watered! It will grow while you play.");
      } else if (this.d.worldTime - s.planted >= 45) {
        if (!transact(this.game.save.data, {}, { wheat: 3, seeds: 2 }))
          return this.toast("Make room in your backpack first.");
        s.planted = null;
        s.water = false;
        this.a.harvests++;
        this.a.flags.harvest = true;
        this.toast("Harvest! +3 wheat and +2 seeds.");
      } else
        this.toast(
          `Growing… ${Math.ceil(45 - this.d.worldTime + s.planted)} seconds of playing to go.`,
        );
      this.syncDevices();
      this.persist();
      return;
    }
    if (id === B.FURNACE)
      return this.open(
        "🔥 Furnace",
        `<p>Smelt 2 raw iron ore with 1 coal to make 2 iron. Glass needs 2 sand and 1 coal. Cooking is instant.</p><p>Raw ore: ${amount(this.d, "ore")} · Coal: ${amount(this.d, "coal")}</p>${btn("Smelt iron", "smelt", `data-id="iron"`)}${btn("Make glass", "smelt", `data-id="glass"`)}`,
      );
    if (id === B.SIGN_BOARD)
      return this.open(
        "✏️ Name your place",
        `<label>Write up to 40 letters<input id="sign-text" maxlength="40" value="${escapeHtml(s.text || this.homeSign())}" autocomplete="off"></label>${btn("Save my sign", "sign", "", "btn-green")}`,
      );
    if (id === B.CHEST) return this.chestPanel(key);
    if (id === B.PORTAL_FRAME) {
      this.open(
        "🌀 A portal to the sky",
        `<p>After your first dragon flight, these stones can take you to the Sky Garden.</p>${btn("Travel to the Sky Garden", "portal", "", "btn-green")}`,
      );
      return;
    }
    this.toast("Connect this block to a switch with glow wire.");
  }
  chestPanel(key) {
    const s = this.a.devices[key];
    this.panel = { type: "device", key };
    const list = Object.keys(ITEMS).filter(
      (id) =>
        ITEMS[id].kind !== "tool" && (amount(this.d, id) || s.stash?.[id]),
    );
    this.open(
      "📦 Storage chest",
      `<p>Store materials here, then take them out whenever you need them. Empty a chest before removing it.</p><div class="chest-list">${list.map((id) => `<div><strong>${ITEMS[id].icon} ${ITEMS[id].name}</strong><span>Bag ${amount(this.d, id)} · Chest ${s.stash?.[id] || 0}</span>${btn("Store 1", "stash", `data-id="${id}" data-out="0" ${!amount(this.d, id) ? "disabled" : ""}`)}${btn("Take 1", "stash", `data-id="${id}" data-out="1" ${!s.stash?.[id] ? "disabled" : ""}`)}</div>`).join("")}</div>`,
    );
  }
  afterPlace(b) {
    if (DEVICE_IDS.has(b[3])) {
      const s = deviceState(this.d, b);
      if (b[3] === B.SIGN_BOARD) s.text = this.homeSign();
    }
    this.syncDevices();
  }
  beforeRemove(b) {
    if (!canRemoveDevice(this.d, b)) {
      this.toast("Empty the chest before you remove it.");
      return false;
    }
    return true;
  }
  afterRemove(b) {
    delete this.a.devices[cellKey(b)];
    this.syncDevices();
  }
  syncDevices() {
    const power = circuitPower(this.d.blocks, this.a.devices);
    this.power = power;
    const present = new Set();
    for (const b of this.d.blocks) {
      if (!DEVICE_IDS.has(b[3]) && b[3] !== B.TORCH) continue;
      const k = cellKey(b);
      present.add(k);
      const s = b[3] === B.TORCH ? {} : deviceState(this.d, b),
        lit = power.has(k);
      // Sign wording stays private to its owner, so signs can't become a way to send messages.
      const signText = this.E.session ? `${this.E.session.ownerName}’s sign` : s.text || this.homeSign();
      if (b[3] === B.DOOR || b[3] === B.DRAWBRIDGE) {
        let next =
          s.on || lit
            ? b[3] === B.DOOR
              ? B.DOOR_OPEN
              : B.DRAWBRIDGE_OPEN
            : b[3];
        const p = this.E.player;
        if (
          b[3] === B.DOOR &&
          next === B.DOOR &&
          p.pos.x + p.hw > b[0] &&
          p.pos.x - p.hw < b[0] + 1 &&
          p.pos.z + p.hw > b[2] &&
          p.pos.z - p.hw < b[2] + 1 &&
          p.pos.y + p.h > b[1] &&
          p.pos.y < b[1] + 1
        )
          next = B.DOOR_OPEN;
        if (
          this.E.world.get(...b.slice(0, 3)) &&
          this.E.world.get(...b.slice(0, 3)) !== next
        )
          this.E.world.set(...b.slice(0, 3), next, true);
      }
      const version = JSON.stringify([
        b[3],
        signText,
        s.planted === null
          ? null
          : Math.floor(Math.min(1, (this.d.worldTime - s.planted) / 45) * 3),
        s.water,
        s.on,
        lit,
      ]);
      const old = this.visuals.get(k);
      if (old?.version === version) continue;
      if (old) {
        old.obj.removeFromParent();
        if (old.obj.userData.tag) old.obj.userData.tag.material.map.dispose();
        disposeObj(old.obj);
        this.visuals.delete(k);
      }
      const obj = new THREE.Group();
      obj.position.set(b[0] + 0.5, b[1], b[2] + 0.5);
      if (b[3] === B.SIGN_BOARD) {
        const tag = nameTag(signText, {
          color: "#fff2ce",
          crown: false,
          scale: 2,
        });
        tag.position.y = 1.15;
        obj.add(tag);
        obj.userData.tag = tag;
      }
      if (b[3] === B.FARM_PLOT && s.planted !== null) {
        const growth = s.water
          ? Math.min(1, (this.d.worldTime - s.planted) / 45)
          : 0.15;
        for (let i = 0; i < 5; i++)
          box(
            obj,
            [0.08, 0.2 + growth * 0.65, 0.08],
            [
              ((i % 3) - 1) * 0.22,
              0.3 + growth * 0.3,
              Math.floor(i / 3) * 0.3 - 0.15,
            ],
            growth >= 1 ? "#e8c353" : "#70b756",
          );
      }
      if (b[3] === B.CIRCUIT_LAMP) {
        const m = box(
          obj,
          [1.01, 1.01, 1.01],
          [0, 0.5, 0],
          lit ? "#ffde6a" : "#675f52",
        );
        m.material.emissive.set(lit ? "#d5852d" : "#000000");
        m.material.emissiveIntensity = 0.6;
      }
      if (b[3] === B.SWITCH)
        box(
          obj,
          [0.15, 0.35, 0.15],
          [s.on ? 0.15 : -0.15, 0.3, 0],
          s.on ? "#8af77b" : "#c29c63",
        );
      if (b[3] === B.WIRE && lit) {
        const m = box(obj, [0.85, 0.025, 0.16], [0, 0.075, 0], "#ffd776");
        m.material.emissive.set("#bf3814");
      }
      if (b[3] === B.TORCH) {
        const fire = box(obj, [0.23, 0.2, 0.23], [0, 0.77, 0], "#ffd778");
        fire.material.emissive.set("#ffa840");
      }
      if (obj.children.length) {
        this.group.add(obj);
        this.visuals.set(k, { obj, version });
      }
    }
    for (const [k, v] of this.visuals)
      if (!present.has(k)) {
        v.obj.removeFromParent();
        if (v.obj.userData.tag) v.obj.userData.tag.material.map.dispose();
        disposeObj(v.obj);
        this.visuals.delete(k);
      }
    this.E.world.rebuild();
  }
  mountDragon() {
    if (
      !this.d.dragonHatched ||
      !this.a.flags.cave ||
      !this.a.flags.flightLesson ||
      !amount(this.d, "saddle") ||
      !this.d.blocks.some((b) => b[3] === B.PERCH)
    )
      return this.toast("Complete Ember’s flight quest first.");
    if (
      !boxFree(
        this.E.world,
        this.E.player.pos.x - 0.5,
        this.E.player.pos.y + 2,
        this.E.player.pos.z - 0.5,
        this.E.player.pos.x + 0.5,
        this.E.player.pos.y + 6,
        this.E.player.pos.z + 0.5,
      ) ||
      this.E.player.pos.y < 35
    )
      return this.toast("Ember needs open sky. Walk out of the cave first.");
    this.close();
    this.E.cancelGather();
    this.mount = { land: false };
    this.a.flags.flew = true;
    this.a.flights++;
    this.E.player.vel.set(0, 0, 0);
    this.dragon.visible = true;
    this.persist();
    this.toast(
      "You’re flying! Move to steer. Hold Jump to climb. Use / Land descends.",
    );
  }
  land() {
    if (this.mount) this.mount.land = true;
  }
  stopRide() {
    this.mount = null;
    this.rail = null;
    this.dragon.visible = false;
    this.cart.visible = false;
    this.E.player.vel.set(0, 0, 0);
    this.E.player.invuln = 2;
  }
  startCart(b) {
    b = this.d.blocks.find((q) => cellKey(q) === cellKey(b));
    if (!b) return;
    if (railNeighbours(this.d.blocks, b).length === 0)
      return this.toast(
        "Join at least two rails side by side. Slopes can climb one block.",
      );
    this.E.cancelGather();
    this.rail = { from: b, to: null, previous: null, t: 0 };
    this.cart.visible = true;
    this.a.flags.railRide = true;
    this.persist();
    this.toast("All aboard! Steer at forks. Use / Leave to get out.");
  }
  exitCart() {
    this.rail = null;
    this.cart.visible = false;
    this.E.player.vel.set(0, 0, 0);
    this.E.player.invuln = 2;
  }
  drive(dt, input) {
    const p = this.E.player;
    if (this.mount) {
      const yaw = this.game.camRig.yaw,
        x = Math.sin(yaw) * input.moveY - Math.cos(yaw) * input.moveX,
        z = Math.cos(yaw) * input.moveY + Math.sin(yaw) * input.moveX;
      this.E.stream.update(p.pos.x + x, p.pos.z + z);
      p.vel.set(x * 12, this.mount.land ? -7 : input.jumpHeld ? 8 : 0, z * 12);
      moveBody(
        p,
        this.E.world,
        this.E.colliders,
        p.vel.x * dt,
        p.vel.y * dt,
        p.vel.z * dt,
      );
      if (!this.mount) return true;
      if (p.pos.y > 90) p.pos.y = 90;
      if (Math.hypot(x, z) > 0.1) p.facing = Math.atan2(x, z);
      if (p.onGround && this.mount.land) {
        this.stopRide();
        this.toast("A perfect landing!");
      } else if (p.onGround) p.pos.y += 0.15;
      this.dragon.position.copy(p.pos).add(v3(0, -0.45, 0));
      this.dragon.rotation.y = p.facing;
      this.dragon.userData.wings?.forEach(
        (w, i) =>
          (w.rotation.z = Math.sin(this.time * 9) * 0.55 * (i ? 1 : -1)),
      );
      p.ch.root.position.copy(p.pos);
      p.ch.root.rotation.y = p.facing;
      p.ch.update(dt, { grounded: true, riding: true });
      return true;
    }
    if (this.rail) {
      const r = this.rail;
      if (!this.d.blocks.includes(r.from)) {
        this.exitCart();
        return false;
      }
      if (!r.to) {
        let ns = railNeighbours(this.d.blocks, r.from).filter(
          (b) => b !== r.previous,
        );
        if (!ns.length) ns = railNeighbours(this.d.blocks, r.from);
        if (!ns.length) {
          this.exitCart();
          return false;
        }
        const fx = Math.sin(p.facing),
          fz = Math.cos(p.facing);
        ns.sort((a, b) => {
          const score = (q) =>
            (q[0] - r.from[0]) * fx +
            (q[2] - r.from[2]) * fz +
            input.moveX *
              ((q[0] - r.from[0]) * fz - (q[2] - r.from[2]) * fx) *
              3;
          return score(b) - score(a);
        });
        r.to = ns[0];
        r.t = 0;
      }
      const speed = r.from[3] === B.POWERED_RAIL ? 9 : 4;
      r.t +=
        (dt * speed) /
        Math.hypot(
          r.to[0] - r.from[0],
          r.to[1] - r.from[1],
          r.to[2] - r.from[2],
        );
      const t = Math.min(1, r.t),
        next = v3(r.from[0] + 0.5, r.from[1] + 0.15, r.from[2] + 0.5).lerp(
          v3(r.to[0] + 0.5, r.to[1] + 0.15, r.to[2] + 0.5),
          t,
        );
      if (r.to[1] !== r.from[1]) next.y = Math.max(r.from[1], r.to[1]) + 0.15;
      if (
        !boxFree(
          this.E.world,
          next.x - 0.3,
          next.y,
          next.z - 0.3,
          next.x + 0.3,
          next.y + 1.8,
          next.z + 0.3,
        )
      ) {
        this.exitCart();
        this.toast("The track needs headroom. Clear the blocks above it.");
        return false;
      }
      p.pos.copy(next);
      p.facing = Math.atan2(r.to[0] - r.from[0], r.to[2] - r.from[2]);
      this.cart.position.copy(p.pos);
      this.cart.rotation.y = p.facing;
      p.ch.root.position.copy(p.pos).add(v3(0, 0.2, 0));
      p.ch.root.rotation.y = p.facing;
      p.ch.update(dt, { grounded: true, riding: true });
      p.vel.set(0, 0, 0);
      if (t === 1) {
        r.previous = r.from;
        r.from = r.to;
        r.to = null;
      }
      return true;
    }
    return false;
  }
  adopt(animal) {
    if (this.E.session)
      return this.toast("Animals can join your farm in your own world.");
    if (this.a.animals.length >= 16)
      return this.toast("Your farm has 16 animals. Enjoy caring for them!");
    if (!["cow", "sheep"].includes(animal.kind))
      return this.toast("Use a lead on a sheep or a cow.");
    this.a.animals.push({
      id: Math.max(-1, ...this.a.animals.map((a) => a.id)) + 1,
      kind: animal.kind,
      pos: animal.obj.position.toArray(),
      baby: 0,
      fed: false,
      ready: 0,
    });
    this.d.hunted[animal.id] = this.d.worldTime + 150;
    animal.obj.visible = false;
    this.persist();
    this.toast(
      "A new farm friend! Hold wheat or a lead and it will follow you.",
    );
  }
  animalAction(animal) {
    const it = selectedItem(this.d);
    if (it?.id === "lead") {
      this.adopt(animal);
      return true;
    }
    if (it?.id === "shears" && animal.kind === "sheep") {
      if ((animal.shearedUntil || 0) > this.d.worldTime) {
        this.toast("This sheep is growing new wool.");
        return true;
      }
      if (addItem(this.d, "wool", 3)) {
        animal.shearedUntil = this.d.worldTime + 90;
        this.persist();
        this.toast("+3 wool. The sheep is happy!");
      }
      return true;
    }
    return false;
  }
  farmAction() {
    const p = this.E.player.pos;
    const a = this.a.animals.find((a) => v3(...a.pos).distanceTo(p) < 3.5);
    if (!a) return false;
    this.panel = { type: "animal", id: a.id };
    this.open(
      `${a.kind === "chicken" ? "🐔" : a.kind === "sheep" ? "🐑" : "🐄"} Your ${a.baby ? "baby " : ""}${a.kind}`,
      `<p>${a.baby ? "Growing up while you play." : a.fed ? "Fed and happy! Feed another adult of the same kind for a baby." : "Feed with 1 wheat. Two fed adults of the same kind can have a baby."}</p>${btn("Feed 1 wheat", "feed", `${a.baby ? "disabled" : ""}`)}${a.kind === "sheep" ? btn("Shear wool", "shear") : a.kind === "chicken" ? btn("Collect an egg", "egg") : ""}<p>Hold wheat or a lead to bring your animals into a fenced pen. Put the item away so they stay there.</p>`,
    );
    return true;
  }
  scout() {
    if (this.game.save.data.equip.pet !== "dog")
      return this.toast("Choose Scout in the Locker to sniff out treasure.");
    if (this.a.scoutAt > this.d.worldTime)
      return this.toast(
        "Scout is resting his nose. Explore a little and try again.",
      );
    const p = this.E.player.pos;
    this.scoutTreasure = p.clone().add(v3(4, 0, 3));
    this.a.scoutAt = this.d.worldTime + 120;
    this.E.waypoint = {
      x: this.scoutTreasure.x,
      z: this.scoutTreasure.z,
      name: "Scout’s buried treasure",
      icon: "🐾",
    };
    this.toast(
      "Woof! Scout found a scent. Follow the sparkles and press Use there.",
    );
    this.persist();
  }
  shoot() {
    if (this.time < (this.shotAt ?? -1) + BOW.cooldown) return;
    if (!transact(this.game.save.data, { arrow: 1 }))
      return this.toast("Craft arrows from wood and stone, anywhere!");
    this.shotAt = this.time;
    const p = this.E.player,
      cam = this.game.camera,
      yaw = this.game.camRig.yaw;
    let o, dir;
    if (this.game.camRig.firstPerson) {
      // Eye view keeps the crosshair ray.
      dir = cam.getWorldDirection(v3()).normalize();
      o = cam.getWorldPosition(v3()).addScaledVector(dir, 0.3);
    } else {
      // Third person aims at the helped target's middle, otherwise straight along the camera.
      const target = this.aimTarget();
      o = p.pos.clone().add(v3(0, 1.2, 0));
      dir = target
        ? aimPoint(target).sub(o).normalize()
        : v3(Math.sin(yaw), 0, Math.cos(yaw));
      p.facing = Math.atan2(dir.x, dir.z);
    }
    this.launch(o, dir);
    audio.play("swing");
    this.persist();
  }
  // One arrow in flight from o along dir (no arrow is spent here; shoot() pays for it).
  launch(o, dir) {
    const obj = arrowModel(),
      d = dir.clone().normalize();
    obj.position.copy(o);
    obj.quaternion.setFromUnitVectors(v3(0, 0, 1), d);
    this.group.add(obj);
    this.arrowShots.push({ obj, dir: d, life: BOW.life });
    return obj;
  }
  // Monsters an arrow can hit. Another kind of monster only needs a Mob-style body {pos, hw, h},
  // obj.visible and hit(dir, damage) to be shot, aimed at and ringed.
  targets() {
    return [...this.E.enemies.values()].filter(
      (m) => !m.dead && !m.dying && m.obj.visible,
    );
  }
  // An unblocked straight line between two points: the same wall test arrows use in flight.
  clearShot(a, b) {
    const d = b.clone().sub(a),
      len = d.length();
    return (
      len < 1e-6 ||
      !this.E.world.raycast(a.x, a.y, a.z, d.x, d.y, d.z, len, (id) => BLOCKS[id].solid)
    );
  }
  // Third-person aim help: the nearest awake monster within 14 blocks and ±35° of the camera, with a clear shot.
  aimTarget() {
    const p = this.E.player.pos,
      yaw = this.game.camRig.yaw,
      o = p.clone().add(v3(0, 1.2, 0));
    let best = null,
      near = BOW.range;
    for (const m of this.targets()) {
      const q = m.body.pos,
        dx = q.x - p.x,
        dz = q.z - p.z,
        flat = Math.hypot(dx, dz),
        dist = q.distanceTo(p);
      if (
        dist < near &&
        flat > 0.01 &&
        (dx * Math.sin(yaw) + dz * Math.cos(yaw)) / flat >= BOW.cone &&
        this.clearShot(o, aimPoint(m))
      ) {
        best = m;
        near = dist;
      }
    }
    return best;
  }
  // A missed arrow stays where it landed (only in memory, never saved) until it is picked up or expires.
  dropArrow(obj, pos, dir) {
    obj.position.copy(pos);
    obj.quaternion.setFromUnitVectors(v3(0, 0, 1), dir);
    this.arrowDrops.push({
      obj,
      until: this.d.worldTime + BOW.lasts,
      ready: this.time + BOW.ready,
    });
    while (this.arrowDrops.length > BOW.keep) this.removeDrop(0);
  }
  removeDrop(i) {
    const [a] = this.arrowDrops.splice(i, 1);
    a.obj.removeFromParent();
    disposeObj(a.obj);
  }
  clearArrows() {
    for (const shot of this.arrowShots) {
      shot.obj.removeFromParent();
      disposeObj(shot.obj);
    }
    this.arrowShots = [];
    while (this.arrowDrops.length) this.removeDrop(0);
  }
  updateArrows(dt) {
    const solid = (id) => BLOCKS[id].solid;
    for (let i = this.arrowShots.length - 1; i >= 0; i--) {
      const shot = this.arrowShots[i],
        from = shot.obj.position.clone(),
        step = dt * BOW.speed,
        wall = this.E.world.raycast(from.x, from.y, from.z, ...shot.dir.toArray(), step, solid),
        to = from.clone().addScaledVector(shot.dir, wall ? wall.dist : step);
      shot.life -= dt;
      // The first monster the arrow reaches on this frame's path, never through a wall.
      let hit = null,
        first = Infinity;
      for (const m of this.targets()) {
        const t = arrowEntry(from, to, m);
        if (t >= 0 && t < first) {
          hit = m;
          first = t;
        }
      }
      shot.obj.position.copy(to);
      if (!hit && !wall && shot.life > 0) continue;
      this.arrowShots.splice(i, 1);
      if (hit) {
        hit.hit(shot.dir, BOW.damage);
        shot.obj.removeFromParent();
        disposeObj(shot.obj);
      } else if (wall)
        // Stuck in the block, tip just inside.
        this.dropArrow(shot.obj, to.addScaledVector(shot.dir, -0.3), shot.dir);
      else this.fallArrow(shot);
    }
  }
  // Out of puff: the arrow drops to the ground (or a pond's surface) below, tip first.
  fallArrow(shot) {
    const q = shot.obj.position,
      ground = this.E.world.raycast(q.x, q.y, q.z, 0, -1, 0, 64, (id) => BLOCKS[id].solid || !!BLOCKS[id].liquid);
    if (ground)
      return this.dropArrow(
        shot.obj,
        q.clone().setY(q.y - ground.dist + 0.22),
        v3(shot.dir.x, -1.5, shot.dir.z).normalize(),
      );
    shot.obj.removeFromParent();
    disposeObj(shot.obj);
  }
  // Walking within 1.3 blocks of a dropped arrow (anywhere from feet to head) puts it back in the bag.
  updateDrops() {
    const p = this.E.player.pos;
    let got = 0;
    for (let i = this.arrowDrops.length - 1; i >= 0; i--) {
      const a = this.arrowDrops[i],
        q = a.obj.position;
      if (a.until <= this.d.worldTime) {
        this.removeDrop(i);
        continue;
      }
      const dy = q.y - Math.min(Math.max(q.y, p.y), p.y + 1.8);
      if (
        this.time >= a.ready &&
        Math.hypot(q.x - p.x, dy, q.z - p.z) < BOW.pickup &&
        addItem(this.d, "arrow", 1)
      ) {
        this.E.lootVisual("arrow", 1, q.clone());
        this.removeDrop(i);
        got++;
      }
    }
    if (got) {
      audio.play("pop");
      this.persist();
    }
  }
  action(op, d = {}) {
    const E = this.E,
      a = this.a,
      s = this.game.save.data;
    if (op === "close") return this.close();
    if (op === "quests") return this.questPanel();
    if (op === "readQuest")
      return audio.speak(
        nextQuest(this.d, s.name).title + ". " + nextQuest(this.d, s.name).text,
      );
    if (op === "map") {
      this.close();
      return E.journal();
    }
    if (op === "questTrail" || op === "trail") {
      const q =
        op === "questTrail" ? nextQuest(this.d).target : LANDMARKS[d.id];
      this.close();
      E.waypoint = q;
      E.refreshHUD();
      return;
    }
    if (op === "camera") {
      a.firstPerson = !a.firstPerson;
      this.game.camRig.setDistance(a.firstPerson ? 0 : 7.5);
      this.game.camRig.firstPerson = a.firstPerson;
      this.close();
      this.persist();
      return;
    }
    if (op === "answer") return this.answer(d.answer);
    if (op === "hint" && this.panel?.type === "puzzle")
      return this.renderPuzzle(PUZZLES[this.panel.id].hint);
    if (op === "readPuzzle" && this.panel?.type === "puzzle")
      return audio.speak(PUZZLES[this.panel.id].text);
    if (op === "gear") return this.gearPanel();
    if (op === "flightLesson") return this.puzzle("flight");
    if (op === "mount") return this.mountDragon();
    if (op === "land") return this.land();
    if (op === "exitCart") return this.exitCart();
    if (op === "scout") {
      this.close();
      return this.scout();
    }
    if (op === "trick") {
      this.trickTime = 2;
      this.close();
      E.waveTime = E.time + 2;
      E.fx.particles.confetti(E.pet.obj.position, 35, 1);
      audio.play("powerup");
      return;
    }
    if (op === "interact")
      return this.interact(this.context(E.targetCell(true)));
    if (op === "equip" && this.panel?.type === "gear" && amount(this.d, d.id)) {
      if (d.id === "jump_boots") a.gear.boots = !a.gear.boots;
      else if (d.id === "shield") a.gear.shield = !a.gear.shield;
      else if (["armour_iron", "armour_diamond"].includes(d.id))
        a.gear.armour = a.gear.armour === d.id ? null : d.id;
      this.persist();
      return this.gearPanel();
    }
    if (
      op === "enchant" &&
      this.panel?.type === "gear" &&
      ["frost", "fortune"].includes(d.id)
    ) {
      if (!this.nearestEnchant())
        return this.toast("Stand beside an enchanting table first.");
      if (!a.flags[d.id]) return this.puzzle(d.id);
      if (!a.enchant[d.id] && transact(s, {}, {}, 100, 3)) {
        a.enchant[d.id] = true;
        this.persist();
      } else this.toast("Enchanting needs 100 coins and 3 gems.");
      return this.gearPanel();
    }
    if (op === "portal") {
      if (!a.flags.flew)
        return this.toast("Earn your first dragon flight first.");
      const far = E.player.pos.x < 80;
      const x = far && E.player.pos.y > 60 ? 112 : 48;
      this.close();
      this.stopRide();
      E.stream.update(x, 48, true);
      E.player.spawn(v3(x + 0.5, 69, 48.5));
      this.game.camRig.snap(E.player.pos);
      return;
    }
    if (this.panel?.type === "device") {
      const key = this.panel.key,
        b = this.d.blocks.find((b) => cellKey(b) === key),
        dev = a.devices[key];
      if (!b || !dev) return; // check the actual saved device, never trust a UI item id
      if (op === "smelt" && b[3] === B.FURNACE) {
        const ok =
          d.id === "iron"
            ? transact(s, { ore: 2, coal: 1 }, { iron: 2 })
            : d.id === "glass"
              ? transact(s, { sand: 2, coal: 1 }, { glass: 4 })
              : false;
        if (ok) {
          this.persist();
          audio.play("powerup");
        } else this.toast("Check your ingredients and backpack space.");
        return this.device(key);
      }
      if (op === "sign" && b[3] === B.SIGN_BOARD) {
        dev.text =
          document.querySelector("#sign-text")?.value.slice(0, 40) ||
          this.homeSign();
        this.syncDevices();
        this.persist();
        return this.close();
      }
      if (op === "stash" && b[3] === B.CHEST) {
        if (!transferChest(this.d, key, d.id, 1, d.out === "1"))
          this.toast("That item cannot be moved right now.");
        this.persist();
        return this.chestPanel(key);
      }
    }
    if (this.panel?.type === "animal") {
      const animal = a.animals.find((v) => v.id === this.panel.id);
      if (!animal) return;
      if (
        op === "feed" &&
        !animal.baby &&
        !animal.fed &&
        transact(s, { wheat: 1 })
      ) {
        animal.fed = true;
        const other = a.animals.find(
          (v) =>
            v !== animal &&
            v.kind === animal.kind &&
            !v.baby &&
            v.fed &&
            v3(...v.pos).distanceTo(v3(...animal.pos)) < 8,
        );
        if (other && a.animals.length < 16) {
          a.animals.push({
            id: Math.max(-1, ...this.a.animals.map((a) => a.id)) + 1,
            kind: animal.kind,
            pos: animal.pos.slice(),
            baby: 90,
            fed: false,
            ready: 0,
          });
          other.fed = false;
          animal.fed = false;
          this.toast("A baby! Your animal family is growing.");
        }
      } else if (
        op === "shear" &&
        animal.kind === "sheep" &&
        amount(this.d, "shears") &&
        !animal.ready &&
        addItem(this.d, "wool", 3)
      ) {
        animal.ready = 90;
        this.toast("+3 wool!");
      } else if (
        op === "egg" &&
        animal.kind === "chicken" &&
        !animal.baby &&
        !animal.ready &&
        addItem(this.d, "egg", 1)
      ) {
        animal.ready = 90;
        this.toast("+1 egg. Use it from your hotbar to hatch a chick!");
      } else
        this.toast(
          "Check your wheat or shears. Animals need time to grow and rest.",
        );
      this.persist();
      return this.close();
    }
  }
  useHeld() {
    const it = selectedItem(this.d);
    if (it?.id === "bow") {
      this.shoot();
      return true;
    }
    if (it?.id === "shield") {
      this.E.player.power.shield = 1;
      return true;
    }
    if (it?.id === "egg") {
      if (this.a.animals.length >= 16) return true;
      if (transact(this.game.save.data, { egg: 1 })) {
        this.a.animals.push({
          id: Math.max(-1, ...this.a.animals.map((a) => a.id)) + 1,
          kind: "chicken",
          pos: this.E.player.pos.toArray(),
          baby: 60,
          ready: 0,
          fed: false,
        });
        this.persist();
        this.toast("Peep! Your chick will grow while you play.");
      }
      return true;
    }
    if (
      this.scoutTreasure &&
      this.scoutTreasure.distanceTo(this.E.player.pos) < 3
    ) {
      if (transact(this.game.save.data, {}, { crystal: 2, egg: 1 })) {
        this.a.flags.scout = true;
        this.scoutTreasure = null;
        this.persist();
        this.toast("Scout found 2 crystals and a chicken egg!");
      }
      return true;
    }
    return false;
  }
  afterCamera(dt, input) {
    const first = this.game.camRig.firstPerson;
    this.E.character.root.visible = !first;
    this.cross.style.display = first ? 'block' : 'none';
    if (!this.mount && this.a.firstPerson !== first) {
      this.a.firstPerson = first;
      this.persist();
      if (first && !this.game.eyeHelpShown) {
        this.game.eyeHelpShown = true;
        this.toast(`👀 Eye view · zoom out to see ${escapeHtml(this.game.save.data.name)} again`);
      }
    }
    const it = selectedItem(this.d),
      id =
        this.game.camRig.firstPerson && it && amount(this.d, it.id)
          ? it.id
          : null;
    if (!this.handView) {
      this.handView = new FirstPersonHand(this.E.character, this.game.mats);
      this.viewHand = this.handView.root;
      this.group.add(this.viewHand);
    }
    this.handView.update(dt, this.game.camera, this.E.player, input, {
      first, id, shotAge: this.time - (this.shotAt ?? -100),
    });
    // Bow aim help in third person: a pulsing ring under the target, and the action label says SHOOT 🎯.
    this.aim =
      !first && !this.mount && !this.rail && !this.E.building && it?.id === "bow" && amount(this.d, "bow") && amount(this.d, "arrow")
        ? this.aimTarget()
        : null;
    this.aimRing.visible = !!this.aim;
    if (this.aim) {
      const b = this.aim.body,
        pulse = Math.sin(this.time * 7),
        ground = this.E.world.raycast(b.pos.x, b.pos.y + 0.3, b.pos.z, 0, -1, 0, 6, (id) => BLOCKS[id].solid);
      this.aimRing.position.set(b.pos.x, ground ? b.pos.y + 0.36 - ground.dist : b.pos.y + 0.06, b.pos.z);
      this.aimRing.scale.setScalar((b.hw + 0.55) * (1 + pulse * 0.12));
      this.aimRing.material.opacity = 0.65 + pulse * 0.25;
    }
  }
  update(dt, input) {
    this.time += dt;
    const mining = this.E.mining;
    this.cracks.visible = !!mining;
    if (mining) {
      this.cracks.position.set(
        mining.cell[0] + 0.5,
        mining.cell[1] + 0.5,
        mining.cell[2] + 0.5,
      );
      this.cracks.geometry.setDrawRange(
        0,
        Math.max(2, Math.floor((mining.elapsed / mining.seconds) * 60) * 2),
      );
    }
    const p = this.E.player;
    if (input.wasPressed("v")) {
      this.a.firstPerson = !this.a.firstPerson;
      this.game.camRig.setDistance(this.a.firstPerson ? 0 : 7.5);
      this.persist();
    }
    if (input.wasPressed("q")) this.questPanel();
    if (selectedItem(this.d)?.id === "shield" && input.actionHeld)
      p.power.shield = 0.3;
    if (this.a.gear.shield && input.wasPressed("f")) p.power.shield = 2;
    if (this.trickTime > 0) {
      this.trickTime -= dt;
      this.E.pet.model.rotation.x = (2 - this.trickTime) * Math.PI;
    } else this.E.pet.model.rotation.x = 0;
    this.E.pet.obj.visible = !this.mount;
    this.E.pet.model.scale.setScalar(
      this.E.pet.kind === "dragon"
        ? this.a.flags.flew
          ? 1.5
          : this.a.flags.cave
            ? 1.2
            : 1
        : 1,
    );
    for (const m of this.markers) {
      m.g.visible =
        p.pos.distanceTo(m.g.position) < 60 &&
        !(m.id === "vault" && this.a.flags.cave);
      m.tag.visible = p.pos.distanceTo(m.g.position) < 30;
      if (m.jewel) {
        m.jewel.rotation.y += dt;
        m.jewel.position.y = 1.5 + Math.sin(this.time * 2) * 0.12;
      }
    }
    for (const v of this.visuals.values())
      v.obj.visible = v.obj.position.distanceTo(p.pos) < 60;
    if (Math.floor(this.time) !== this.tick) {
      this.tick = Math.floor(this.time);
      this.syncDevices();
      this.applyGear();
    }
    for (const a of this.a.animals) {
      a.baby = Math.max(0, a.baby - dt);
      a.ready = Math.max(0, a.ready - dt);
      let model = this.farmModels.get(a.id);
      if (!model) {
        model = animalModel(a.kind);
        if (a.kind === "chicken") {
          model.mats.body.color.set("#fff9e2");
          model.mats.face.color.set("#edbd55");
        }
        this.group.add(model.root);
        this.farmModels.set(a.id, model);
      }
      model.root.visible = v3(...a.pos).distanceTo(p.pos) < 55;
      model.root.scale.setScalar(
        (a.kind === "chicken" ? 0.6 : 1) * (a.baby ? 0.55 : 1),
      );
      const attract =
        ["wheat", "lead"].includes(selectedItem(this.d)?.id) &&
        v3(...a.pos).distanceTo(p.pos) < 10;
      if (attract && v3(...a.pos).distanceTo(p.pos) > 2) {
        const dir = p.pos
          .clone()
          .sub(v3(...a.pos))
          .setY(0)
          .normalize();
        const next = v3(...a.pos).addScaledVector(dir, dt * 2);
        let y = Math.floor(a.pos[1]) + 1;
        while (
          y > Math.floor(a.pos[1]) - 2 &&
          !this.E.world.solidF(next.x, y - 1, next.z)
        )
          y--;
        if (
          boxFree(
            this.E.world,
            next.x - 0.35,
            y,
            next.z - 0.35,
            next.x + 0.35,
            y + 1.5,
            next.z + 0.35,
          ) &&
          Math.abs(y - a.pos[1]) <= 1
        )
          a.pos = [next.x, y, next.z];
        model.root.rotation.y = Math.atan2(dir.x, dir.z);
        model.legs.forEach(
          (l, i) =>
            (l.rotation.x = Math.sin(this.time * 8 + i * Math.PI) * 0.3),
        );
      }
      model.root.position.fromArray(a.pos);
    }
    this.updateArrows(dt);
    this.updateDrops();
    // Gentle day/sunset cycle; caves stay legible and torches/enchanted picks cast warm light.
    const phase = (this.d.worldTime % 480) / 480,
      sunset = Math.max(0, Math.sin(phase * Math.PI * 2 - Math.PI / 2));
    const u = this.game.sky.uniforms;
    u.topColor.value
      .set("#70b5e7")
      .lerp(new THREE.Color("#6574ad"), sunset * 0.65);
    u.horizonColor.value
      .set("#d0ecfa")
      .lerp(new THREE.Color("#ffa76d"), sunset * 0.8);
    this.game.sun.intensity = 2.6 - sunset * 0.9;
    this.game.sun.color
      .set("#fff1d6")
      .lerp(new THREE.Color("#ffbd81"), sunset * 0.5);
    const torch = this.d.blocks.find(
      (b) =>
        [B.TORCH, B.LAMP].includes(b[3]) &&
        p.pos.distanceTo(v3(b[0], b[1], b[2])) < 10,
    );
    const glow =
      this.a.enchant.fortune && selectedItem(this.d)?.tool === "pick";
    this.light.intensity = torch || glow || p.pos.y < 34 ? 4 : 0;
    this.light.position.copy(
      torch
        ? v3(torch[0] + 0.5, torch[1] + 1, torch[2] + 0.5)
        : p.pos.clone().add(v3(0, 1.5, 0)),
    );
  }
  dispose() {
    this.stopRide();
    this.clearArrows();
    this.aim = null;
    this.handView?.dispose();
    this.cross.remove();
    this.game.camRig.firstPerson = false;
    this.game.camRig.allowFirstPerson = false;
    this.game.camRig.setDistance(this.game.camRig.dist);
    this.E.character.root.visible = true;
    if (this.armour) {
      this.armour.removeFromParent();
      disposeObj(this.armour);
    }
    for (const m of this.markers) m.tag.material.map.dispose();
    for (const v of this.visuals.values())
      if (v.obj.userData.tag) v.obj.userData.tag.material.map.dispose();
    this.group.removeFromParent();
    disposeObj(this.group);
  }
}
